import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Save, ArrowLeft, Droplet } from 'lucide-react';

const DonorForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [fetchingDonor, setFetchingDonor] = useState(false);
  const [error, setError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [donorId, setDonorId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bloodGroup: '',
    age: '',
    gender: '',
    address: '',
    state: '',
    city: '',
    pincode: '',
    isAvailable: true,
    weight: '',
    allergies: '',
    medicalConditions: '',
    lastHealthCheck: '',
    password: 'Donor@123' // Default password for new donors
  });

  // Extract donor ID from URL query params if present
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');
    
    if (id) {
      setIsEdit(true);
      setDonorId(id);
      fetchDonorDetails(id);
    }
  }, [location]);

  const fetchDonorDetails = async (id) => {
    try {
      setFetchingDonor(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors/${id}`,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      
      const { user, profile } = response.data;
      console.log('Fetched donor details:', response.data);
      
      // Determine the blood group from either user or profile
      const bloodGroup = user.bloodGroup || (profile && profile.bloodType) || '';
      
      // Handle address from both user and profile
      let address = user.address || '';
      let state = user.state || '';
      let city = user.city || '';
      let pincode = user.pincode || '';
      
      // If user address is empty but profile has address
      if (!address && profile && profile.address) {
        if (typeof profile.address === 'object') {
          address = profile.address.street || '';
          state = state || profile.address.state || '';
          city = city || profile.address.city || '';
          pincode = pincode || profile.address.zipCode || '';
        } else if (typeof profile.address === 'string') {
          address = profile.address;
        }
      }
      
      // Handle gender from both user and profile
      let gender = user.gender || '';
      if (!gender && profile && profile.gender) {
        // Convert donor profile gender format to user gender format if needed
        const genderMap = {
          'male': 'Male',
          'female': 'Female',
          'other': 'Other'
        };
        gender = genderMap[profile.gender] || profile.gender;
      }
      
      // Handle age from both user and profile
      let age = user.age || '';
      if (!age && profile && profile.age) {
        age = profile.age;
      }
      
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || (profile && profile.phone) || '',
        bloodGroup: bloodGroup,
        age: age,
        gender: gender,
        address: address,
        state: state,
        city: city,
        pincode: pincode,
        isAvailable: profile?.isAvailable !== undefined ? profile.isAvailable : true,
        weight: profile?.weight || '',
        allergies: profile?.allergies || '',
        medicalConditions: profile?.medicalConditions || '',
        lastHealthCheck: profile?.lastHealthCheck ? new Date(profile.lastHealthCheck).toISOString().split('T')[0] : '',
        password: 'Donor@123' // Default password, will not be used in edit mode
      });
      
      console.log('Setting form data with state:', state, 'and city:', city);
      
      setFetchingDonor(false);
    } catch (err) {
      console.error('Error fetching donor details:', err);
      setError('Failed to fetch donor details. Please try again.');
      setFetchingDonor(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Phone must be 10 digits';
    if (!formData.bloodGroup) newErrors.bloodGroup = 'Blood group is required';
    
    // Only validate password for new donors
    if (!isEdit && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setError(Object.values(newErrors)[0] || null);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Validate form
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Create payload with all form data
      const payload = { ...formData };
      
      // Log what we're sending to the server
      console.log('Submitting donor data:', payload);
      
      let response;
      if (isEdit) {
        // Update existing donor
        response = await axios.put(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors/${donorId}`,
          payload,
          {
            headers: {
              'x-auth-token': token,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new donor
        response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/admin/donors`,
          payload,
          {
            headers: {
              'x-auth-token': token,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      console.log('Server response:', response.data);
      
      // Redirect to donor list on success
      navigate('/admin/dashboard/donors', { 
        state: { 
          message: isEdit ? 'Donor updated successfully' : 'Donor created successfully',
          type: 'success'
        } 
      });
    } catch (err) {
      console.error('Error submitting donor form:', err);
      setError(err.response?.data?.message || 'Failed to save donor. Please try again.');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // For checkboxes, use the checked property
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  const handleCancel = () => {
    navigate('/admin/dashboard/donors');
  };

  if (fetchingDonor) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{isEdit ? 'Edit Donor' : 'Add New Donor'}</h2>
        <button 
          onClick={() => navigate('/admin/dashboard/donors')}
          className="flex items-center px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Donors
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Personal Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="age" className="block text-sm font-medium text-gray-700">Age</label>
                    <input
                      type="number"
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      min="18"
                      max="65"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Gender</label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="bloodGroup" className="block text-sm font-medium text-gray-700 flex items-center">
                    <Droplet className="h-4 w-4 text-red-500 mr-1" />
                    Blood Group
                  </label>
                  <select
                    id="bloodGroup"
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                  {!formData.bloodGroup && (
                    <p className="mt-1 text-xs text-red-500">Blood group is required for proper donor matching</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="weight" className="block text-sm font-medium text-gray-700">Weight (kg)</label>
                  <input
                    type="number"
                    id="weight"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    min="45"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  />
                </div>
                
                {/* Password field - only for new donors */}
                {!isEdit && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="Enter password"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Default password: Donor@123 (Minimum 6 characters)
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Contact Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Contact Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="Enter state"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                      placeholder="Enter city"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="pincode" className="block text-sm font-medium text-gray-700">Pincode</label>
                  <input
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Medical Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Medical Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="allergies" className="block text-sm font-medium text-gray-700">Allergies (if any)</label>
                <textarea
                  id="allergies"
                  name="allergies"
                  rows="3"
                  value={formData.allergies}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="List any allergies..."
                ></textarea>
              </div>
              
              <div>
                <label htmlFor="medicalConditions" className="block text-sm font-medium text-gray-700">Medical Conditions (if any)</label>
                <textarea
                  id="medicalConditions"
                  name="medicalConditions"
                  rows="3"
                  value={formData.medicalConditions}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="List any medical conditions..."
                ></textarea>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="lastHealthCheck" className="block text-sm font-medium text-gray-700">Last Health Check Date</label>
                <input
                  type="date"
                  id="lastHealthCheck"
                  name="lastHealthCheck"
                  value={formData.lastHealthCheck}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                />
              </div>
              
              <div className="flex items-center h-full pt-6">
                <input
                  type="checkbox"
                  id="isAvailable"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-900">
                  Available for Donation
                </label>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  {isEdit ? 'Update Donor' : 'Save Donor'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DonorForm;